import { Id, toast } from 'react-toastify';
import { toTitleCase } from 'utils/lodash';
import { generateUUID } from 'utils/uuid';

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warn',
  DEFAULT = 'default',
}

export type ToastTypeConfig = {
  type: ToastType;
  title: string;
};

type ToastMethod = keyof Pick<
  typeof toast,
  'success' | 'error' | 'info' | 'warn'
>;

const MAX_ACTIVE_TOASTS = 1;
const activeToastIds = new Set<Id>();

export const createToast =
  (type: ToastType, title: string) => (message: string) => {
    const toastId = generateUUID();

    if (activeToastIds.size >= MAX_ACTIVE_TOASTS) {
      toast.clearWaitingQueue();
      return;
    }

    const toastBody = (
      <div>
        <strong>{title}</strong>
        <div>{message}</div>
      </div>
    );
    const toastOptions = {
      toastId,
      onOpen: () => {
        activeToastIds.add(toastId);
      },
      onClose: () => {
        activeToastIds.delete(toastId);
        activeToastIds.delete(id);
      },
    };
    const id =
      type === ToastType.DEFAULT
        ? toast(toastBody, toastOptions)
        : toast[type as ToastMethod](toastBody, toastOptions);

    activeToastIds.add(id);
  };

const createToastConfig = ({ type, title }: ToastTypeConfig) =>
  createToast(type, title);

const successToast = createToastConfig({
  type: ToastType.SUCCESS,
  title: toTitleCase(ToastType.SUCCESS),
});

const errorToast = createToastConfig({
  type: ToastType.ERROR,
  title: toTitleCase(ToastType.ERROR),
});

const infoToast = createToastConfig({
  type: ToastType.INFO,
  title: toTitleCase(ToastType.INFO),
});

const warningToast = createToastConfig({
  type: ToastType.WARNING,
  title: toTitleCase(ToastType.WARNING),
});

const defaultToast = createToastConfig({
  type: ToastType.DEFAULT,
  title: 'Title',
});

function useToast() {
  return {
    [ToastType.SUCCESS]: successToast,
    [ToastType.ERROR]: errorToast,
    [ToastType.INFO]: infoToast,
    [ToastType.WARNING]: warningToast,
    [ToastType.DEFAULT]: defaultToast,
    toast,
  };
}

export default useToast;
